/* eslint-disable camelcase */
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Bar,
  BarChart,
  type BarShapeProps,
  CartesianGrid,
  LabelList,
  type LabelProps,
  Legend,
  Line,
  LineChart,
  Rectangle,
  ResponsiveContainer,
  type ScaleFunction,
  Tooltip,
  useXAxisScale,
  useYAxisScale,
  XAxis,
  YAxis,
} from 'recharts'

import { type Metric, metricLabels } from './exploration'
import { dimensionLabels, dimensionValueLabel, formatCount, formatDuration, percentage } from './model'
import { MAX_TREND_GROUPS } from './sharedSplits'
import { foldVersionTail } from './versionTail'

import styles from './AttributionObservabilityPage.module.scss'

import type { AttributionGroupedTrend, AttributionOverview } from '@/api/attributionObservability'
import DataTable from '@/components/DataTable'

const MAX_LABELLED_DAYS = 45
const COLORS = [
  '#034f46',
  '#64548c',
  '#ab5223',
  '#276db0',
  '#a84065',
  '#637c24',
  '#887019',
  '#296d76',
  '#8e4493',
  '#4d5d86',
  '#b34a40',
  '#4f7354',
]
// Pointer distance, in pixels, within which a line or dot counts as hovered.
const LINE_HOVER_RADIUS = 10
type DailySummary = AttributionOverview['trend'][number]['summary']
type Point = Record<string, string | number | null>
type TooltipProps = {
  active?: boolean
  label?: unknown
  payload?: ReadonlyArray<{
    dataKey?: unknown
    value?: unknown
    payload?: Point
  }>
}
type Scales = { x: ScaleFunction; y: ScaleFunction }
// `index` is null when a whole series is hovered (a line or a legend entry) rather than one bar chunk.
type Hovered = { key: string; index: number | null } | null

export const dailyMetricValue = (summary: DailySummary, metric: Metric): number | null => {
  if (metric === 'summary_speaker_chips_named')
    return percentage(summary.named_speaker_chip_count ?? null, summary.speaker_chip_count ?? null)
  if (metric === 'meetings') return summary.meeting_count
  if (metric === 'labels') return percentage(summary.machine_labeled_speakers, summary.active_speakers)
  if (metric === 'labels_by_words') return percentage(summary.machine_labeled_words, summary.words)
  if (metric === 'ax') return percentage(summary.ax_covered_speech_ms, summary.speech_ms)
  if (metric === 'ax_capture') return percentage(summary.ax_readable_ms, summary.ax_capture_ms)
  return percentage(summary.fully_machine_labeled_meetings ?? null, summary.measured_label_meetings ?? null)
}

const dailyMetricDetail = (summary: DailySummary | undefined, metric: Metric): string | null => {
  if (!summary || metric === 'meetings') return null
  if (metric === 'summary_speaker_chips_named')
    return `${formatCount(summary.named_speaker_chip_count ?? 0)} / ${formatCount(summary.speaker_chip_count ?? 0)} speaker chips`
  const [numerator, denominator, unit] =
    metric === 'labels'
      ? ([summary.machine_labeled_speakers, summary.active_speakers, 'speakers'] as const)
      : metric === 'labels_by_words'
        ? ([summary.machine_labeled_words, summary.words, 'words'] as const)
        : metric === 'ax'
          ? ([summary.ax_covered_speech_ms, summary.speech_ms, 'duration'] as const)
          : metric === 'ax_capture'
            ? ([summary.ax_readable_ms, summary.ax_capture_ms, 'duration'] as const)
            : ([summary.fully_machine_labeled_meetings, summary.measured_label_meetings, 'meetings'] as const)
  if (numerator == null || denominator == null) return null
  return unit === 'duration'
    ? `${formatDuration(numerator)} / ${formatDuration(denominator)}`
    : `${formatCount(numerator)} / ${formatCount(denominator)} ${unit}`
}

const seriesName = (path: AttributionGroupedTrend['groups'][number]['path']) =>
  path.length
    ? path
        .map(({ dimension, value }) => `${dimensionLabels[dimension]}: ${dimensionValueLabel(dimension, value)}`)
        .join(' · ')
    : 'All matching meetings'
const colorIndex = (identity: string) => {
  let hash = 0
  for (const char of identity) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  return hash % COLORS.length
}

// Recharts only exposes axis scales inside the chart, so this hands them out for pointer hit-testing.
const ScaleProbe = ({ onScales }: { onScales: (scales: Scales | null) => void }) => {
  const x = useXAxisScale()
  const y = useYAxisScale()
  useEffect(() => onScales(x && y ? { x, y } : null), [x, y, onScales])
  return null
}

const distanceToSegment = (px: number, py: number, x0: number, y0: number, x1: number, y1: number) => {
  const dx = x1 - x0
  const dy = y1 - y0
  const t = dx || dy ? Math.min(1, Math.max(0, ((px - x0) * dx + (py - y0) * dy) / (dx * dx + dy * dy))) : 0
  return Math.hypot(px - (x0 + t * dx), py - (y0 + t * dy))
}

const nearestLine = (px: number, py: number, scales: Scales, points: Point[], keys: string[]): string | null => {
  let nearest: string | null = null
  let best = LINE_HOVER_RADIUS
  for (const key of keys) {
    const coords = points.map((point) => {
      const value = point[key]
      const x = scales.x(point.date)
      const y = typeof value === 'number' ? scales.y(value) : undefined
      return x === undefined || y === undefined ? null : ([x, y] as const)
    })
    coords.forEach((start, index) => {
      if (!start) return
      const end = coords[index + 1] ?? start
      const distance = distanceToSegment(px, py, start[0], start[1], end[0], end[1])
      if (distance <= best) {
        best = distance
        nearest = key
      }
    })
  }
  return nearest
}

const Trend = ({
  trend,
  groups: rawGroups,
  truncated = false,
  startAt,
  endAt,
  metric,
}: {
  trend: AttributionOverview['trend']
  groups?: AttributionGroupedTrend['groups']
  truncated?: boolean
  startAt: string
  endAt: string
  metric: Metric
}) => {
  const showMeetings = metric === 'meetings'
  const [hovered, setHovered] = useState<Hovered>(null)
  // Keyed by group path, since series keys are positional and shift when the groups change.
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set())
  const [activeDate, setActiveDate] = useState<string | null>(null)
  const scales = useRef<Scales | null>(null)
  const onScales = useCallback((next: Scales | null) => {
    scales.current = next
  }, [])
  const groups = useMemo(() => rawGroups && foldVersionTail(rawGroups), [rawGroups])
  const colors = new Map<string, string>()
  const used = new Set<number>()
  for (const identity of (groups ?? []).map((group) => JSON.stringify(group.path)).sort()) {
    let index = colorIndex(identity)
    while (used.has(index) && used.size < COLORS.length) index = (index + 1) % COLORS.length
    used.add(index)
    colors.set(identity, COLORS[index])
  }
  const subtitle: Record<Metric, string> = {
    meetings: 'meeting count',
    labels: 'share of active speakers',
    labels_by_words: 'share of spoken words',
    fully_named: 'share of measured meetings',
    summary_speaker_chips_named: 'share of summary speaker chips',
    ax: 'share of speech duration',
    ax_capture: 'share of active recording time',
  }
  const series = groups
    ? groups.map((group, index) => ({
        key: `series${index}`,
        label: seriesName(group.path),
        identity: JSON.stringify(group.path),
        legendLabel:
          group.path.map(({ dimension, value }) => dimensionValueLabel(dimension, value)).join(' · ') ||
          'All matching meetings',
        trend: group.trend,
        color: colors.get(JSON.stringify(group.path)) ?? COLORS[0],
      }))
    : [
        {
          key: 'total',
          label: metricLabels[metric],
          identity: 'total',
          legendLabel: metricLabels[metric],
          trend,
          color: COLORS[0],
        },
      ]
  const isHidden = (key: unknown) => series.some((item) => item.key === key && hidden.has(item.identity))
  const toggleHidden = (key: unknown) => {
    const identity = series.find((item) => item.key === key)?.identity
    if (identity === undefined) return
    setHovered(null)
    setHidden((current) => {
      const next = new Set(current)
      if (!next.delete(identity)) next.add(identity)
      return next
    })
  }
  const lookup = series.map((item) => new Map(item.trend.map((point) => [point.date, point.summary])))
  const points: Point[] = []
  const end = new Date(endAt).getTime()
  const firstDay = new Date(startAt).toISOString().slice(0, 10)
  for (let day = Date.parse(`${firstDay}T00:00:00Z`); day < end; day += 86_400_000) {
    const date = new Date(day).toISOString().slice(0, 10)
    points.push(
      Object.fromEntries([
        ['date', date],
        ...series.flatMap((item, index) => {
          const summary = lookup[index].get(date)
          return [
            [item.key, summary ? dailyMetricValue(summary, metric) : showMeetings ? 0 : null],
            [`${item.key}Detail`, dailyMetricDetail(summary, metric)],
          ]
        }),
      ])
    )
  }
  const format = (value: unknown) =>
    typeof value === 'number' ? (showMeetings ? formatCount(value) : `${value.toFixed(1)}%`) : 'Not measured'
  const hasMeasurements = series.some((item) =>
    item.trend.some((point) => dailyMetricValue(point.summary, metric) !== null)
  )
  const missingFullyNamed =
    metric === 'fully_named' &&
    series.some((item) =>
      item.trend.some(
        ({ summary }) =>
          summary.measured_label_meetings === undefined || summary.fully_machine_labeled_meetings === undefined
      )
    )
  const columns: ColumnDef<(typeof points)[number]>[] = [
    { accessorKey: 'date', header: 'UTC date' },
    ...series.map((item) => ({
      accessorKey: item.key,
      header: item.label,
      cell: ({ row, getValue }: { row: { original: (typeof points)[number] }; getValue: () => unknown }) => (
        <span className={styles.ratio}>
          <strong>{format(getValue())}</strong>
          {row.original[`${item.key}Detail`] && <small>{row.original[`${item.key}Detail`]}</small>}
        </span>
      ),
    })),
  ]
  const isEmphasized = (key: string, index?: number) =>
    hovered?.key === key && (hovered.index === null || index === undefined || hovered.index === index)
  const isDimmed = (key: string, index?: number) => hovered !== null && !isEmphasized(key, index)
  const labelsValues = (key: string) => hovered?.key === key && hovered.index === null
  const labelFormat = (value: unknown) =>
    typeof value === 'number' && (value > 0 || !showMeetings) ? format(value) : ''
  const trackLine = (event: MouseEvent<HTMLDivElement>) => {
    // The legend sets its own hover; nearest-line tracking there would clear it.
    if (showMeetings || (event.target as Element).closest('.recharts-legend-wrapper')) return
    const surface = event.currentTarget.querySelector('.recharts-wrapper > svg')
    if (!scales.current || !surface) return
    const { left, top } = surface.getBoundingClientRect()
    const key = nearestLine(
      event.clientX - left,
      event.clientY - top,
      scales.current,
      points,
      series.map((item) => item.key).filter((key) => !isHidden(key))
    )
    setHovered((current) =>
      current?.key === key && current.index === null ? current : key ? { key, index: null } : null
    )
  }
  const countWithShare = (value: number, total: number) => {
    const share = percentage(value, total)
    return share === null ? formatCount(value) : `${formatCount(value)} · ${share.toFixed(1)}%`
  }
  // Beside the chunk rather than inside it, so thin chunks stay readable.
  const chunkLabel = ({ viewBox, value, index }: LabelProps) => {
    if (typeof value !== 'number' || value <= 0 || index === undefined || !viewBox || !('width' in viewBox)) return null
    const total = series.reduce(
      (sum, item) => sum + (isHidden(item.key) ? 0 : Number(points[index]?.[item.key]) || 0),
      0
    )
    return (
      <text
        x={viewBox.x + viewBox.width + 6}
        y={viewBox.y + viewBox.height / 2}
        dominantBaseline="central"
        fontSize={11}
        fontWeight={600}
        fill="#1a1a1a"
        stroke="#fff"
        strokeWidth={3}
        paintOrder="stroke"
      >
        {countWithShare(value, total)}
      </text>
    )
  }
  const tooltipContent = ({ active, label, payload }: TooltipProps) => {
    if (!active || !payload?.length) return null
    const rows = payload.flatMap((entry) => {
      const item = series.find((candidate) => candidate.key === entry.dataKey)
      return item && !isHidden(item.key)
        ? [
            {
              item,
              value: entry.value,
              detail: entry.payload?.[`${item.key}Detail`],
            },
          ]
        : []
    })
    const dayTotal = rows.reduce((sum, row) => sum + (typeof row.value === 'number' ? row.value : 0), 0)
    // Stacked bars list top-down to match the stack; lines list in the order they appear, highest first.
    if (showMeetings) rows.reverse()
    else
      rows.sort((a, b) => (typeof b.value === 'number' ? b.value : -1) - (typeof a.value === 'number' ? a.value : -1))
    return (
      <div className={styles.tooltip}>
        <p className={styles.tooltipLabel}>{String(label)}</p>
        {rows.map(({ item, value, detail }) => (
          <p
            key={item.key}
            className={isEmphasized(item.key) ? styles.tooltipItemActive : undefined}
            style={{ color: item.color }}
          >
            {showMeetings ? item.legendLabel : item.label} :{' '}
            {showMeetings && typeof value === 'number'
              ? countWithShare(value, dayTotal)
              : detail
                ? `${format(value)} (${detail})`
                : format(value)}
          </p>
        ))}
      </div>
    )
  }
  const activePoint = showMeetings ? undefined : points.find((point) => point.date === activeDate)
  // Recharts sorts legend entries ascending, so higher values get lower keys and unmeasured series go last.
  const legendOrder = (entry: { dataKey?: unknown }) => {
    const value = activePoint?.[String(entry.dataKey)]
    return typeof value === 'number' ? -value : Number.POSITIVE_INFINITY
  }
  const Chart = showMeetings ? BarChart : LineChart
  const title = `${metricLabels[metric]} over time`
  return (
    <section className={styles.panel} aria-label={title}>
      <div className={styles.sectionHeader}>
        <h2>{title}</h2>
        <span className={styles.muted}>Daily · UTC · {subtitle[metric]}</span>
      </div>
      {truncated && (
        <p role="status">
          Showing the {MAX_TREND_GROUPS} largest combined groups for this date range. Narrow the filters to compare
          smaller groups.
        </p>
      )}
      {missingFullyNamed ? (
        <p className={styles.empty}>
          Daily fully named meeting data is unavailable on this backend. The updated API is required.
        </p>
      ) : hasMeasurements ? (
        <>
          <div className={styles.chart} onMouseMove={trackLine} onMouseLeave={() => setHovered(null)}>
            <ResponsiveContainer width="100%" height={320}>
              <Chart
                data={points}
                margin={{ top: 20, right: showMeetings ? 88 : 24, bottom: 0, left: 0 }}
                accessibilityLayer
                onMouseMove={(state) => setActiveDate(state.activeLabel == null ? null : String(state.activeLabel))}
                onMouseLeave={() => setActiveDate(null)}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(value: string) => value.slice(5)} />
                <YAxis
                  domain={showMeetings ? [0, 'auto'] : [0, 100]}
                  allowDecimals={!showMeetings}
                  tickFormatter={format}
                  width={72}
                />
                {/* Opaque and above the legend, so the legend text never shows through the hover card. A bar
                    tooltip anchors at the bar's center, so the larger offset clears the bar's other half.
                    filterNull is off so unmeasured lines still get their "Not measured" row. */}
                <Tooltip
                  wrapperStyle={{ zIndex: 10 }}
                  content={tooltipContent}
                  offset={showMeetings ? 40 : 10}
                  filterNull={false}
                />
                <Legend
                  itemSorter={activePoint ? legendOrder : 'value'}
                  wrapperStyle={{ cursor: 'pointer' }}
                  onClick={(entry) => toggleHidden(entry.dataKey)}
                  onMouseEnter={(entry) =>
                    !isHidden(entry.dataKey) && setHovered({ key: String(entry.dataKey), index: null })
                  }
                  onMouseLeave={() => setHovered(null)}
                  formatter={(label: string, entry) => {
                    const text = showMeetings
                      ? (series.find((item) => item.label === label)?.legendLabel ?? label)
                      : label
                    return hovered?.key === entry.dataKey ? <strong>{text}</strong> : text
                  }}
                />
                {!showMeetings && <ScaleProbe onScales={onScales} />}
                {series.map((item) =>
                  showMeetings ? (
                    <Bar
                      key={item.key}
                      dataKey={item.key}
                      name={item.label}
                      fill={item.color}
                      hide={hidden.has(item.identity)}
                      stackId="meetings"
                      maxBarSize={56}
                      isAnimationActive={false}
                      onMouseEnter={(_, index) => setHovered({ key: item.key, index })}
                      onMouseLeave={() => setHovered(null)}
                      shape={(props: BarShapeProps) => (
                        <Rectangle
                          {...props}
                          fillOpacity={isDimmed(item.key, props.index) ? 0.35 : 1}
                          stroke={isEmphasized(item.key, props.index) ? '#1a1a1a' : 'none'}
                          strokeWidth={1.5}
                        />
                      )}
                    >
                      {labelsValues(item.key) && <LabelList dataKey={item.key} content={chunkLabel} />}
                    </Bar>
                  ) : (
                    <Line
                      key={item.key}
                      dataKey={item.key}
                      name={item.label}
                      stroke={item.color}
                      hide={hidden.has(item.identity)}
                      strokeWidth={isEmphasized(item.key) ? 3 : 1}
                      strokeOpacity={isDimmed(item.key) ? 0.25 : 1}
                      dot={{
                        r: isEmphasized(item.key) ? 3.5 : 2,
                        strokeOpacity: isDimmed(item.key) ? 0.25 : 1,
                      }}
                      activeDot={{ r: isEmphasized(item.key) ? 6 : 4 }}
                      isAnimationActive={false}
                      connectNulls={false}
                    >
                      {((series.length === 1 && points.length <= MAX_LABELLED_DAYS) || labelsValues(item.key)) && (
                        <LabelList
                          dataKey={item.key}
                          position="top"
                          formatter={labelFormat}
                          fill={item.color}
                          fontSize={labelsValues(item.key) ? 11 : 10}
                          fontWeight={labelsValues(item.key) ? 600 : undefined}
                        />
                      )}
                    </Line>
                  )
                )}
              </Chart>
            </ResponsiveContainer>
          </div>
          <details className={styles.detail}>
            <summary>{showMeetings ? 'View daily values' : 'View daily counts and denominators'}</summary>
            <DataTable data={points} columns={columns} ariaLabel="Daily measurements" />
          </details>
        </>
      ) : (
        <p className={styles.empty}>No measurements for this metric in this date range.</p>
      )}
    </section>
  )
}

export { Trend }
